"use client";

import { useMemo } from "react";
import { format, parse } from "date-fns";
import type { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import type { MarketplaceDateFilterValue } from "@/types/api/marketplace";
import { filterPrimaryButtonClass } from "./filterStyles";
import { MarketplaceRangeCalendar } from "./MarketplaceRangeCalendar";

interface MarketplaceDatePickerPanelProps {
  label: string;
  value?: MarketplaceDateFilterValue;
  onChange: (value: MarketplaceDateFilterValue | undefined) => void;
  onApply?: () => void;
  className?: string;
}

function parseDate(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toIso(date?: Date): string | undefined {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd");
}

export function MarketplaceDatePickerPanel({
  label,
  value,
  onChange,
  onApply,
  className,
}: MarketplaceDatePickerPanelProps) {
  const selected: DateRange | undefined = useMemo(
    () => ({
      from: parseDate(value?.start),
      to: parseDate(value?.end),
    }),
    [value?.start, value?.end]
  );

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange(undefined);
      return;
    }
    onChange({
      start: toIso(range.from),
      end: toIso(range.to ?? range.from),
    });
  };

  const handleClear = () => {
    onChange(undefined);
    onApply?.();
  };

  const footerRangeLabel =
    selected?.from && selected?.to
      ? `${format(selected.from, "MMM d")} – ${format(selected.to, "MMM d")}`
      : null;

  return (
    <div className={cn("flex flex-col", className)}>
      <MarketplaceRangeCalendar
        selected={selected}
        onSelect={handleSelect}
        defaultMonth={selected?.from ?? new Date()}
      />

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={handleClear}
          className="cursor-pointer text-sm font-medium text-gray-600 underline hover:text-gray-900">
          Clear dates
        </button>
        {footerRangeLabel ? (
          <span className="text-brand-700 shrink-0 text-sm font-semibold">
            {footerRangeLabel}
          </span>
        ) : (
          <span className="text-sm text-gray-500">{label}</span>
        )}
        <button
          type="button"
          onClick={onApply}
          className={cn(filterPrimaryButtonClass, "shrink-0 px-6")}>
          Apply
        </button>
      </div>
    </div>
  );
}
